import React, { useEffect, useRef, useState } from 'react';

function SpiralProjectsNew({ onProjectClick, onSeeAllClick, setScrollCounter }) {
  const [activeCard, setActiveCard] = useState(0);
  const containerRef = useRef(null);

  const projects = [
    {
      id: 24,
      image: "/assets/RIPL%20Projects/Retrofitting/State%20Bank%20Bhavan%2C%20Nariman%20Point%2C%20Mumbai/1.jpg",
      title: "STATE BANK BHAVAN",
      location: "NARIMAN POINT, MUMBAI",
      description: "Comprehensive retrofitting of an iconic banking headquarters with advanced structural strengthening and modernization."
    },
    {
      id: 15,
      image: "/assets/RIPL%20Projects/Interiors/SBI%20Conference%20Center%2C%20State%20Bank%20Bhavan%2C%20Mumbai/1.png",
      title: "SBI CONFERENCE CENTER",
      location: "STATE BANK BHAVAN, MUMBAI",
      description: "Premium corporate conference facility with state-of-the-art technology integration and sophisticated interior design."
    },
    {
      id: 5,
      image: "/assets/RIPL%20Projects/Construction/LIC%20Mixed%20Use%20Development%2C%20Vadodara%2C%20Gujarat/1.png",
      title: "LIC MIXED USE",
      location: "VADODARA, GUJARAT",
      description: "Expansive mixed-use development combining commercial, residential, and public spaces with modern architectural vision."
    },
    {
      id: 22,
      image: "/assets/RIPL%20Projects/Retrofitting/IGIDR%2C%20Goregaon%2C%20Mumbai/1.png",
      title: "IGIDR CAMPUS",
      location: "GOREGAON, MUMBAI",
      description: "Structural retrofitting and modernization of the prestigious Indira Gandhi Institute of Development Research campus."
    }
  ];

  useEffect(() => {
    const updateSpiral = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const scrollProgress = -containerRect.top / (container.offsetHeight - window.innerHeight);
      const normalizedProgress = Math.max(0, Math.min(1, scrollProgress));

      const totalCards = projects.length + 1;
      const cards = container.querySelectorAll('.project-card-spiral');

      cards.forEach((card, i) => {
        const compactMode = window.innerWidth < 1024;
        const spreadY = compactMode ? 180 : 250;
        const spreadX = compactMode ? 72 : 200;
        const spreadZ = compactMode ? 240 : 400;
        const rotateYFactor = compactMode ? -8 : -15;
        const rotateXFactor = compactMode ? 3 : 5;

        const offset = i - (normalizedProgress * (totalCards - 1));
        const zTranslate = -Math.abs(offset) * spreadZ;
        const yTranslate = offset * spreadY;
        const xTranslate = Math.sin(offset * 0.8) * spreadX;
        const rotateY = offset * rotateYFactor;
        const rotateX = offset * rotateXFactor;

        card.style.transform = `translate3d(${xTranslate}px, ${yTranslate}px, ${zTranslate}px) rotateY(${rotateY}deg) rotateX(${rotateX}deg)`;

        const opacity = compactMode
          ? 1 - (Math.abs(offset) * 0.32)
          : 1 - (Math.abs(offset) * 0.4);

        card.style.opacity = Math.max(compactMode ? 0.18 : 0, opacity);

        if (Math.abs(offset) < 0.4) {
          card.classList.add('active');
          setActiveCard(i);
          if (setScrollCounter) {
            setScrollCounter(`0${i + 1}`);
          }
        } else {
          card.classList.remove('active');
        }
      });
    };

    const handleScroll = () => {
      updateSpiral();
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateSpiral);
    updateSpiral();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateSpiral);
    };
  }, [projects.length, setScrollCounter]);

  return (
    <section id="projects" className="spiral-container" ref={containerRef}>
      <div className="spiral-sticky">
        <div className="spiral-track">
          {projects.map((project, index) => (
            <button
              key={project.id}
              type="button"
              className="project-card-spiral"
              onClick={() => onProjectClick(project.id)}
            >
              <img
                src={project.image}
                alt={project.title}
                loading="lazy"
                decoding="async"
              />
              <div className="project-card-overlay lg:hidden">
                <span className="bank-gothic text-[10px] tracking-[0.45em] text-white/70">
                  PROJECT {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="bank-gothic mt-3 text-2xl leading-[1.05] text-white sm:text-3xl">
                  {project.title}
                </h3>
                <p className="bank-gothic mt-3 text-[10px] tracking-[0.28em] text-[#c49b66] sm:text-[11px]">
                  {project.location}
                </p>
              </div>
            </button>
          ))}

          <button
            type="button"
            className="project-card-spiral see-all-card"
            onClick={onSeeAllClick}
          >
            <div className="flex flex-col items-center text-neutral-400 transition-colors duration-300 hover:text-white">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c49b66" strokeWidth="1.2" className="mb-4">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
              <span className="bank-gothic text-[11px] tracking-[0.3em] text-white">SEE ALL PROJECTS</span>
            </div>
            <div className="project-card-overlay justify-end lg:hidden">
              <span className="bank-gothic text-[10px] tracking-[0.45em] text-white/60">
                PORTFOLIO INDEX
              </span>
              <h3 className="bank-gothic mt-3 text-2xl leading-[1.05] text-white sm:text-3xl">
                SEE ALL PROJECTS
              </h3>
            </div>
          </button>
        </div>

        <div className="project-info-display hidden lg:flex">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className={`project-info-item ${activeCard === index ? 'active' : ''}`}
            >
              <h2 className="bank-gothic mb-4 text-xs tracking-[0.5em] text-neutral-500">
                PROJECT {String(index + 1).padStart(2, '0')}
              </h2>
              <h3 className="bank-gothic text-4xl leading-tight md:text-5xl">
                {project.title} <br />
                <span className="accent-text">{project.location}</span>
              </h3>
              <p className="mt-6 mb-6 max-w-sm text-xs font-light leading-relaxed text-neutral-400">
                {project.description}
              </p>
              <button
                onClick={() => onProjectClick(project.id)}
                className="btn-outline bank-gothic !px-6 !py-2 text-[8px]"
              >
                View Gallery
              </button>
            </div>
          ))}

          <div className={`project-info-item ${activeCard === projects.length ? 'active' : ''}`}>
            <h2 className="bank-gothic mb-4 text-xs tracking-[0.5em] text-neutral-500">PORTFOLIO INDEX</h2>
            <h3 className="bank-gothic text-4xl leading-tight md:text-5xl">
              EXPLORE THE <br />
              <span className="accent-text">ARCHIVE</span>
            </h3>
            <p className="mt-6 mb-6 max-w-sm text-xs font-light leading-relaxed text-neutral-400">
              Discover our complete collection of cultural hubs, high-density residential properties, and industrial infrastructure.
            </p>
            <button
              onClick={onSeeAllClick}
              className="btn-outline bank-gothic !px-6 !py-2 text-[8px]"
            >
              Open Archive Directory {'\u00a0\u2192'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SpiralProjectsNew;
